/**
 * The `/members` checkbox filters, client half. The catalog (which boxes
 * exist, how they group, what they read) comes from the server on every first
 * page (`DataResponse.filter_groups`); the ticked boxes live in the URL as
 * `?filter=<token>,<token>` so a filtered list survives opening a member and
 * coming back, and can be shared as a link. Every ticked box must hold — the
 * server ANDs the tokens (`apps/dashboard/src/repositories/browse-filters.ts`).
 */

import type { DataFilterGroup } from "@layer/dashboard/types";

/** The URL search param carrying the ticked tokens. */
export const FILTER_PARAM = "filter";

/** The ticked tokens in `params`, trimmed, de-duplicated, in URL order. */
export function readFilterTokens(params: URLSearchParams): string[] {
  const raw = params.get(FILTER_PARAM);
  if (raw === null) return [];
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const token = part.trim();
    if (token.length > 0 && !out.includes(token)) out.push(token);
  }
  return out;
}

/** A copy of `params` with `tokens` as the filter (removed when empty). */
export function withFilterTokens(
  params: URLSearchParams,
  tokens: ReadonlyArray<string>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (tokens.length > 0) next.set(FILTER_PARAM, tokens.join(","));
  else next.delete(FILTER_PARAM);
  return next;
}

/** `tokens` with `key` ticked if it was clear, cleared if it was ticked. */
export function toggleFilterToken(
  tokens: ReadonlyArray<string>,
  key: string,
): string[] {
  return tokens.includes(key)
    ? tokens.filter((t) => t !== key)
    : [...tokens, key];
}

/** A run of consecutive groups sharing one panel section. */
export interface FilterSection {
  /** The section's title (`"Tags"`), or `null` for the untitled lead section. */
  readonly label: string | null;
  readonly groups: DataFilterGroup[];
}

/**
 * The catalog cut into sections, in order: a new section starts wherever a
 * group's `section` differs from the one before it.
 */
export function filterSections(
  groups: ReadonlyArray<DataFilterGroup>,
): FilterSection[] {
  const out: FilterSection[] = [];
  for (const g of groups) {
    const label = g.section ?? null;
    const last = out[out.length - 1];
    if (last !== undefined && last.label === label) last.groups.push(g);
    else out.push({ label, groups: [g] });
  }
  return out;
}

/** What a ticked token reads as on its chip. */
export interface FilterChip {
  readonly key: string;
  /** The group heading ("Stage"), or `null` for a token the catalog lacks. */
  readonly group: string | null;
  readonly label: string;
}

/**
 * The chips for `tokens`, in tick order. A token the catalog does not list
 * (a tag nobody carries any more, a catalog not loaded yet) still gets a chip
 * reading its raw token, so a filter that is narrowing the list is never
 * invisible.
 */
export function filterChips(
  groups: ReadonlyArray<DataFilterGroup>,
  tokens: ReadonlyArray<string>,
): FilterChip[] {
  return tokens.map((key) => {
    for (const g of groups) {
      const option = g.options.find((o) => o.key === key);
      if (option !== undefined) {
        return { key, group: g.label, label: option.label };
      }
    }
    return { key, group: null, label: key };
  });
}

/**
 * What a link out of the `/members` list carries to the member page: the
 * list's own URL, filters included. The tokens live only in the list's URL,
 * so without this the page's "← Members" (a plain `/members`) would drop them
 * — only the browser's Back button would find them again.
 */
export interface MembersListState {
  readonly from: string;
}

/** The navigation state for a link out of the list at `pathname` + `search`. */
export function membersListState(
  pathname: string,
  search: string,
): MembersListState {
  return { from: `${pathname}${search}` };
}

/**
 * Where the member page's "← Members" goes: back to the `/members` list it
 * was opened from, filters intact, or the plain list when it was opened from
 * anywhere else (the calendar, the home board, a shared link).
 */
export function membersListHref(state: unknown): string {
  if (typeof state !== "object" || state === null) return "/members";
  const from = (state as { from?: unknown }).from;
  return typeof from === "string" &&
    (from === "/members" || from.startsWith("/members?"))
    ? from
    : "/members";
}
